import {
  Button,
  Combobox,
  type ComboboxOption,
  DateRangeField,
  FilterConditionRow,
  MultiSelect,
  StringSelect,
} from "@tellescope/ui";
import {
  extractMethodFromQueryKey,
  QueryDataForMethod,
  QueryMethodForModel,
  TellescopeQueryKey,
  useAuth,
  useCachedTellescopeQueries,
  useUsersQuery,
} from "@tellescope/tanstack-query";
import { formatName, type FreeformFieldOption, type FreeformFilter } from "@tellescope/utils";
import { Plus } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getContactFreedomFieldOptions, type ContactsFilters } from "./contactFilters";
import { getTagsBehaviorOptions } from "./contactFilterOptions";

export interface ContactsFilterConfigFormProps {
  stagedFilters: ContactsFilters;
  onStagedFiltersChange: (filters: ContactsFilters) => void;
}

export const ContactsFilterConfigForm = ({ stagedFilters, onStagedFiltersChange }: ContactsFilterConfigFormProps) => {
  const { t } = useTranslation("contacts");

  const { selectedOrganization: organization } = useAuth();
  const { data: users = [] } = useUsersQuery();

  const userComboboxOptions = useMemo<ComboboxOption[]>(
    () =>
      users.map((user) => ({
        value: user.id,
        label: formatName(user) || user.id,
      })),
    [users]
  );

  const setFilterField = <K extends keyof ContactsFilters>(key: K, value: ContactsFilters[K]) => {
    onStagedFiltersChange({ ...stagedFilters, [key]: value });
  };

  const selectedCareTeamOptions = useMemo<ComboboxOption[]>(() => {
    const ids = stagedFilters.careTeamIds ?? [];
    return ids.reduce<ComboboxOption[]>((acc, id) => {
      const match = userComboboxOptions.find((u) => u.value === id) ?? { value: id, label: id };
      if (match) {
        acc.push(match);
      }

      return acc;
    }, []);
  }, [stagedFilters.careTeamIds, userComboboxOptions]);

  const customFreeformFieldOptions = useMemo<FreeformFieldOption[]>(() => {
    const customFields = organization?.settings?.endusers?.customFields;
    if (!customFields || !Array.isArray(customFields)) return [];
    return customFields.map((cf) => ({
      value: `fields.${cf.field}`,
      label: cf.field,
      valueType: cf.type === "Number" ? "number" : cf.type === "Date" ? "date" : "string",
    }));
  }, [organization?.settings?.endusers?.customFields]);

  const allFreeformFieldOptions = useMemo<FreeformFieldOption[]>(
    () => [...getContactFreedomFieldOptions(), ...customFreeformFieldOptions],
    [customFreeformFieldOptions]
  );

  const {
    data: { tags, sources },
  } = useCachedTellescopeQueries({
    model: "endusers",
    select: (queries) => {
      const { tagsSet, sourcesSet } = queries.reduce<{ tagsSet: Set<string>; sourcesSet: Set<string> }>(
        (acc, curr) => {
          const method = extractMethodFromQueryKey(
            curr.queryKey as TellescopeQueryKey<"endusers", QueryMethodForModel<"endusers">>
          );
          switch (method) {
            case "getOne": {
              const typedData = curr.state.data as QueryDataForMethod<"endusers", "getOne"> | undefined;
              typedData?.tags?.forEach((tag) => acc.tagsSet.add(tag));
              if (typedData?.source) acc.sourcesSet.add(typedData.source);
              break;
            }
            case "getSome": {
              const typedData = curr.state.data as QueryDataForMethod<"endusers", "getSome"> | undefined;
              typedData?.pages?.forEach((page) =>
                page.forEach((enduser) => {
                  enduser.tags?.forEach((tag) => acc.tagsSet.add(tag));
                  if (enduser.source) acc.sourcesSet.add(enduser.source);
                })
              );
              break;
            }
            case "getByIds": {
              const typedData = curr.state.data as QueryDataForMethod<"endusers", "getByIds"> | undefined;
              typedData?.matches?.forEach((enduser) => {
                enduser.tags?.forEach((tag) => acc.tagsSet.add(tag));
                if (enduser.source) acc.sourcesSet.add(enduser.source);
              });
              break;
            }
          }
          return acc;
        },
        { tagsSet: new Set<string>(), sourcesSet: new Set<string>() }
      );
      return {
        tags: Array.from(tagsSet).map((tag) => ({ label: tag, value: tag })),
        sources: Array.from(sourcesSet).map((source) => ({ label: source, value: source })),
      };
    },
  });

  const handleAddFreeformFilter = () => {
    onStagedFiltersChange({
      ...stagedFilters,
      freeform: [
        ...(stagedFilters.freeform ?? []),
        { field: allFreeformFieldOptions[0]?.value ?? "", operation: "equals", value: "" },
      ],
    });
  };

  const handleUpdateFreeformFilter = (index: number, updated: FreeformFilter) => {
    const freeform = [...(stagedFilters.freeform ?? [])];
    freeform[index] = updated;
    onStagedFiltersChange({ ...stagedFilters, freeform });
  };

  const handleRemoveFreeformFilter = (index: number) => {
    const freeform = (stagedFilters.freeform ?? []).filter((_, i) => i !== index);
    onStagedFiltersChange({ ...stagedFilters, freeform });
  };

  return (
    <div className="space-y-2 font-semibold">
      <h3>{t("Filters")}</h3>
      <div className="space-y-4 font-normal">
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Care Team")}</span>
          <Combobox
            value={selectedCareTeamOptions}
            onChange={(selected) => {
              const ids = Array.isArray(selected) ? selected.map((s) => s.value) : [];
              setFilterField("careTeamIds", ids.length > 0 ? ids : undefined);
            }}
            options={userComboboxOptions}
            multiple
            placeholder={t("Any care team member")}
            searchPlaceholder={t("Search users...")}
            clearable
            className="w-64"
          />
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Tags")}</span>
          <div className="flex items-center gap-2">
            <MultiSelect
              options={tags}
              value={stagedFilters.tags?.tags ?? []}
              onValueChange={(tags) =>
                onStagedFiltersChange({
                  ...stagedFilters,
                  tags: tags.length > 0 ? { tags, behavior: stagedFilters.tags?.behavior ?? "match_any" } : undefined,
                })
              }
              placeholder={t("Enter tags...")}
              className="w-64"
              creator={{
                render: (inputValue) => <span className="text-sm">{t('Add "{{tag}}"', { tag: inputValue })}</span>,
                isValidNewOption: (inputValue) => inputValue.trim().length > 0,
                transform: (inputValue) => inputValue.trim(),
                onCreate: (newOption) => {
                  const existingTags = stagedFilters.tags?.tags ?? [];
                  const tags = [...existingTags, newOption.value];
                  onStagedFiltersChange({
                    ...stagedFilters,
                    tags: { tags, behavior: stagedFilters.tags?.behavior ?? "match_any" },
                  });
                },
              }}
            />
            {(stagedFilters.tags?.tags?.length ?? 0) > 0 && (
              <StringSelect
                value={stagedFilters.tags?.behavior ?? "match_any"}
                onValueChange={(val) =>
                  onStagedFiltersChange({
                    ...stagedFilters,
                    tags: { tags: stagedFilters.tags?.tags ?? [], behavior: val },
                  })
                }
                options={getTagsBehaviorOptions()}
                className="w-36"
              />
            )}
          </div>
        </div>
        <div className="space-y-1.5">
          <DateRangeField
            label={t("Date of Birth")}
            value={stagedFilters.dateOfBirth ?? {}}
            onChange={(value) =>
              setFilterField("dateOfBirth", value.from !== undefined || value.to !== undefined ? value : undefined)
            }
            className="w-64"
          />
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Source")}</span>
          <div className="flex items-center gap-2">
            <MultiSelect
              options={sources}
              value={stagedFilters.source?.sources ?? []}
              onValueChange={(sources) =>
                onStagedFiltersChange({
                  ...stagedFilters,
                  source:
                    sources.length > 0
                      ? { sources, behavior: stagedFilters.source?.behavior ?? "match_any" }
                      : undefined,
                })
              }
              placeholder={t("Enter sources...")}
              className="w-64"
              creator={{
                render: (inputValue) => (
                  <span className="text-sm">{t('Add "{{source}}"', { source: inputValue })}</span>
                ),
                isValidNewOption: (inputValue) => inputValue.trim().length > 0,
                transform: (inputValue) => inputValue.trim(),
                onCreate: (newOption) => {
                  const existingSources = stagedFilters.source?.sources ?? [];
                  const sources = [...existingSources, newOption.value];
                  onStagedFiltersChange({
                    ...stagedFilters,
                    source: { sources, behavior: stagedFilters.source?.behavior ?? "match_any" },
                  });
                },
              }}
            />
            {(stagedFilters.source?.sources?.length ?? 0) > 0 && (
              <StringSelect
                value={stagedFilters.source?.behavior ?? "match_any"}
                onValueChange={(val) =>
                  onStagedFiltersChange({
                    ...stagedFilters,
                    source: {
                      sources: stagedFilters.source?.sources ?? [],
                      behavior: val,
                    },
                  })
                }
                options={getTagsBehaviorOptions()}
                className="w-36"
              />
            )}
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium">{t("Custom Filters")}</p>
          {(stagedFilters.freeform ?? []).length > 0 && (
            <div className="space-y-2">
              {(stagedFilters.freeform ?? []).map((filter, index) => (
                <FilterConditionRow
                  // All other values of `filter` aren't stable
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  filter={filter}
                  fieldOptions={allFreeformFieldOptions}
                  onChange={(updated) => handleUpdateFreeformFilter(index, updated)}
                  onRemove={() => handleRemoveFreeformFilter(index)}
                />
              ))}
            </div>
          )}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={handleAddFreeformFilter}>
            <Plus className="h-4 w-4" />
            {t("Add Filter")}
          </Button>
        </div>
      </div>
    </div>
  );
};
